import { useState } from "react";
import bgLocale from "@fullcalendar/core/locales/bg";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { format } from "date-fns";
import { useNotify } from "react-admin";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import CalendarGlobalStyles from "components/LearningActivity/PreviewWeeklySchedule/calendar/CalendarGlobalStyles";

const OVERLAY_COLOR = "#D77757";

// FullCalendar's own ".fc-v-event .fc-event-main" sets white text, which outranks the black on ".fc-event" in
// CalendarGlobalStyles. eventTextColor is applied inline, so it wins without fighting specificity.
const EVENT_TEXT_COLOR = "#000";

const getMondayDate = date => {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return format(d, "yyyy-MM-dd");
};

const convertToCalendarEvents = (scheduleData, fromDate, { idPrefix = "", backgroundColor } = {}) => {
  const events = [];
  const monday = new Date(fromDate);
  const day = monday.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diffToMonday);

  scheduleData.forEach(item => {
    const eventDate = new Date(monday);
    eventDate.setDate(monday.getDate() + (item.weekDay - 1));
    const dateString = eventDate.toISOString().split("T")[0];

    events.push({
      id: `${idPrefix}${item.id}`,
      title: item.classTypeName ? `${item.disciplineName} (${item.classTypeName})` : item.disciplineName,
      start: `${dateString}T${item.startTime}`,
      end: `${dateString}T${item.endTime}`,
      backgroundColor,
      extendedProps: item,
    });
  });

  return events;
};

const GroupPickerDialog = ({ educationId, row, onSaved, triggerButton }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();

  const [ctx, setCtx] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [studentEvents, setStudentEvents] = useState([]);
  const [overlayEvents, setOverlayEvents] = useState([]);

  const fetchStudentLayer = async (context, date) => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getWeeklyTabbedScheduleCalendar({
          tab: 3,
          studentEducationId: educationId,
          academicYearId: context.academicYearId,
          sessionType: context.sessionType,
          fromDate: date,
        }),
        null,
        "GET",
      );
      setStudentEvents(convertToCalendarEvents(data ?? [], date));
    } catch (err) {
      notifyError(err);
    }
  };

  const fetchOverlayLayer = async (context, date, studyGroup) => {
    if (!studyGroup) {
      setOverlayEvents([]);
      return;
    }
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getWeeklyTabbedScheduleCalendar({
          tab: 4,
          curriculumId: context.curriculumId,
          instructionLanguageId: context.instructionLanguageId,
          course: context.disciplineCourse,
          studyGroup,
          academicYearId: context.academicYearId,
          sessionType: context.sessionType,
          fromDate: date,
        }),
        null,
        "GET",
      );
      // The calendar endpoint ignores the studyGroup parameter for this tab and returns every group of the course,
      // so the chosen group is applied here. Entries without groups are lectures the whole course attends.
      const group = Number(studyGroup);
      const filtered = (data ?? []).filter(
        item =>
          item.disciplineId === row.disciplineId &&
          (!item.studyGroups?.length || item.studyGroups.includes(group)),
      );
      setOverlayEvents(convertToCalendarEvents(filtered, date, { idPrefix: "overlay-", backgroundColor: OVERLAY_COLOR }));
    } catch (err) {
      notifyError(err);
    }
  };

  const handleOpen = async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getMakeupGroupContext(educationId, row.disciplineId, row.semester),
        null,
        "GET",
      );
      setCtx(data);
      const initialDate = getMondayDate(data.sessionStartsOn ?? new Date());
      setFromDate(initialDate);
      setSelectedGroup(data.assignedStudyGroup ?? "");
      await fetchStudentLayer(data, initialDate);
      await fetchOverlayLayer(data, initialDate, data.assignedStudyGroup);
    } catch (err) {
      notifyError(err);
      return false;
    }
  };

  const handleDatesSet = info => {
    const newFromDate = format(info.start, "yyyy-MM-dd");
    if (newFromDate === fromDate) return;
    setFromDate(newFromDate);
    fetchStudentLayer(ctx, newFromDate);
    fetchOverlayLayer(ctx, newFromDate, selectedGroup);
  };

  const handleGroupChange = value => {
    setSelectedGroup(value);
    fetchOverlayLayer(ctx, fromDate, value);
  };

  const handleConfirm = async () => {
    await dataProvider.customRequest(
      studentRequests.assignMakeupGroup(educationId, row.disciplineId, row.semester),
      { studyGroup: Number(selectedGroup) },
      "PUT",
    );
    notify("Групата е записана успешно", { type: "success" });
    onSaved?.();
  };

  const resetState = () => {
    setCtx(null);
    setFromDate(null);
    setSelectedGroup("");
    setStudentEvents([]);
    setOverlayEvents([]);
  };

  const canSave = !ctx?.isLocked && selectedGroup !== "" && Number(selectedGroup) !== ctx?.assignedStudyGroup;

  return (
    <ConfirmModal
      titleContent="Избор на група за отработване"
      dialogSize="lg"
      onOpen={handleOpen}
      onConfirm={handleConfirm}
      resetProps={resetState}
      confirmEnabled={canSave}
      isConfirmButton={!ctx?.isLocked}
      saveTitle="Запази групата"
      triggerButton={triggerButton}
    >
      {ctx?.isLocked ? (
        <Typography
          color="error"
          sx={{ fontWeight: "bold" }}
        >
          Групата не може да бъде променена след началото на семестъра
          {ctx?.assignedStudyGroup ? ` (Група ${ctx.assignedStudyGroup})` : ""}
        </Typography>
      ) : (
        <>
          <TextField
            select
            label="Учебна група"
            size="small"
            value={selectedGroup}
            onChange={e => handleGroupChange(e.target.value)}
            sx={{ mb: 2, minWidth: 160 }}
          >
            {(ctx?.availableGroups ?? []).map(group => (
              <MenuItem
                key={group}
                value={group}
              >
                Група {group}
              </MenuItem>
            ))}
          </TextField>
          <CalendarGlobalStyles />
          <Box sx={{ height: "600px" }}>
            <FullCalendar
              plugins={[timeGridPlugin]}
              headerToolbar={{ left: "prev today next", center: "title", right: "" }}
              initialDate={fromDate}
              datesSet={handleDatesSet}
              dayHeaderFormat={{ weekday: "long", month: undefined, day: undefined }}
              slotMinTime="07:00:00"
              slotMaxTime="21:00:00"
              locale={bgLocale}
              firstDay={1}
              allDaySlot={false}
              height="100%"
              events={[...studentEvents, ...overlayEvents]}
              eventTextColor={EVENT_TEXT_COLOR}
              slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
              eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
            />
          </Box>
        </>
      )}
    </ConfirmModal>
  );
};

export default GroupPickerDialog;
